import React, {
    MouseEvent,
    PropsWithChildren,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react';
import { Chip, IconButton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import {
    CloudDownloadOutlined,
    DeleteOutlined,
    FileOutlined,
    ReloadOutlined,
    SelectOutlined,
} from '@ant-design/icons';
import { gsap } from 'gsap';
import { FileMetadata } from '../../api/types/cms';
import UserItem from '../user/UserItem';

import { useImageBuilder } from '../../utils/image';
import { useDateFormatter } from '../../utils/date';
import { useMeasure, useMedia } from '../../utils/cms';
import { useFileDialog } from './CmsFileDialogs';

import './MasonryFileList.css';

interface GridItem extends FileMetadata {
    x: number;
    y: number;
    w: number;
    h: number;
}

interface MasonryProps {
    items: FileMetadata[];
    ease?: string;
    duration?: number;
    stagger?: number;
    animateFrom?: 'bottom' | 'top' | 'left' | 'right' | 'center' | 'random';
    scaleOnHover?: boolean;
    hoverScale?: number;
    blurToFocus?: boolean;
    colorShiftOnHover?: boolean;
    onClick?: (file: FileMetadata) => void;
    mediaQueries?: Array<string>;
    selectedFiles?: Array<FileMetadata>;
}

const buildColumnArray = (queries: Array<string>) => {
    const cols: Array<number> = [];
    for (let i = queries.length; i > 0; i--) {
        cols.push(i);
    }
    return cols;
};

const MasonryFileList: React.FC<PropsWithChildren<MasonryProps>> = ({
    items,
    ease = 'power3.out',
    duration = 0.6,
    stagger = 0.05,
    animateFrom = 'bottom',
    scaleOnHover = true,
    hoverScale = 0.95,
    blurToFocus = true,
    colorShiftOnHover = false,
    onClick = () => {},
    mediaQueries = [
        '(min-width:2500px)',
        '(min-width:1850px)',
        '(min-width:1300px)',
        '(min-width:600px)',
        '(min-width:400px)',
    ],
    selectedFiles = [],
    children,
}) => {
    const columns = useMedia(mediaQueries, buildColumnArray(mediaQueries), 1);

    const theme = useTheme();
    const { openFileDeleteDialog } = useFileDialog();
    const { buildImageUrl, buildDownloadUrl } = useImageBuilder();
    const { formatDate } = useDateFormatter();
    const [containerRef, { width }] = useMeasure<HTMLDivElement>();

    const getInitialPosition = (item: GridItem) => {
        const containerRect = containerRef.current?.getBoundingClientRect();
        if (!containerRect) return { x: item.x, y: item.y };

        let direction = animateFrom;

        if (animateFrom === 'random') {
            const directions = ['top', 'bottom', 'left', 'right'];
            direction = directions[
                Math.floor(Math.random() * directions.length)
            ] as typeof animateFrom;
        }

        switch (direction) {
            case 'top':
                return { x: item.x, y: -200 };
            case 'bottom':
                return { x: item.x, y: window.innerHeight + 200 };
            case 'left':
                return { x: -200, y: item.y };
            case 'right':
                return { x: window.innerWidth + 200, y: item.y };
            case 'center':
                return {
                    x: containerRect.width / 2 - item.w / 2,
                    y: containerRect.height / 2 - item.h / 2,
                };
            default:
                return { x: item.x, y: item.y + 100 };
        }
    };

    const grid = useMemo<GridItem[]>(() => {
        if (!width) return [];

        const colHeights = new Array(columns).fill(0);
        const columnWidth = width / columns;

        return items.map(child => {
            const col = colHeights.indexOf(Math.min(...colHeights));
            const x = columnWidth * col;

            let height = 150;
            const ratio = child.additional_data?.ratio?.split(':');
            if (ratio && ratio[0] && ratio[1]) {
                height =
                    (columnWidth / parseInt(ratio[0])) * parseInt(ratio[1]);
                if (height < 160) {
                    height = 160;
                }
            }

            const y = colHeights[col];

            colHeights[col] += height;

            return { ...child, x, y, w: columnWidth, h: height };
        });
    }, [columns, items, width]);

    const hasMounted = useRef(false);

    useLayoutEffect(() => {
        grid.forEach((item, index) => {
            const selector = `[data-key="${item.id}"]`;
            const animationProps = {
                x: item.x,
                y: item.y,
                width: item.w,
                height: item.h,
            };

            if (!hasMounted.current) {
                const initialPos = getInitialPosition(item);
                const initialState = {
                    opacity: 0,
                    x: initialPos.x,
                    y: initialPos.y,
                    width: item.w,
                    height: item.h,
                    ...(blurToFocus && { filter: 'blur(10px)' }),
                };

                gsap.fromTo(selector, initialState, {
                    opacity: 1,
                    ...animationProps,
                    ...(blurToFocus && { filter: 'blur(0px)' }),
                    duration: 0.8,
                    ease: 'power3.out',
                    delay: index * stagger,
                });
            } else {
                gsap.to(selector, {
                    ...animationProps,
                    duration: duration,
                    ease: ease,
                    overwrite: 'auto',
                });
            }
        });

        hasMounted.current = true;
    }, [grid, stagger, animateFrom, blurToFocus, duration, ease]);

    const handleMouseEnter = (e: React.MouseEvent, item: GridItem) => {
        const element = e.currentTarget as HTMLElement;
        const selector = `[data-key="${item.id}"]`;

        if (scaleOnHover) {
            gsap.to(selector, {
                scale: hoverScale,
                duration: 0.3,
                ease: 'power2.out',
            });
        }

        const blurBg = element.querySelector('.blur-bg') as HTMLElement;
        if (blurBg) {
            gsap.to(blurBg, {
                opacity: 1,
                duration: 0.3,
            });
        }

        const infoOverlay = element.querySelector(
            '.info-overlay',
        ) as HTMLElement;
        if (infoOverlay) {
            gsap.to(infoOverlay, {
                opacity: 1,
                duration: 0.3,
            });
        }

        if (colorShiftOnHover) {
            const colorOverlay = element.querySelector(
                '.color-overlay',
            ) as HTMLElement;
            if (colorOverlay) {
                gsap.to(colorOverlay, {
                    opacity: 0.3,
                    duration: 0.3,
                });
            }
        }
    };

    const handleMouseLeave = (e: React.MouseEvent, item: GridItem) => {
        const element = e.currentTarget as HTMLElement;
        const selector = `[data-key="${item.id}"]`;

        if (scaleOnHover) {
            gsap.to(selector, {
                scale: 1,
                duration: 0.3,
                ease: 'power2.out',
            });
        }

        const blurBg = element.querySelector('.blur-bg') as HTMLElement;
        if (blurBg) {
            gsap.to(blurBg, {
                opacity: 0,
                duration: 0.3,
            });
        }

        const infoOverlay = element.querySelector(
            '.info-overlay',
        ) as HTMLElement;
        if (infoOverlay) {
            gsap.to(infoOverlay, {
                opacity: 0,
                duration: 0.3,
            });
        }

        if (colorShiftOnHover) {
            const overlay = element.querySelector(
                '.color-overlay',
            ) as HTMLElement;
            if (overlay) {
                gsap.to(overlay, {
                    opacity: 0,
                    duration: 0.3,
                });
            }
        }
    };


    return (
        <div ref={containerRef} className="list">
            {grid.map(item => {
                const selected = Boolean(
                    selectedFiles.find((i: FileMetadata | string) => {
                        if (typeof i === "string") {
                            return i === item.id;
                        }
                        return i.id === item.id
                    }),
                );
                const sameFileId = Boolean(
                    selectedFiles.find((i: FileMetadata | string) => {
                        if (typeof i === "string") {
                            return i === item.file_id;
                        }
                        return i.file_id === item.file_id;
                    }),
                );

                console.log(item.id, selected);

                const downloadFile = (event: MouseEvent) => {
                    event.stopPropagation();
                    window.open(buildDownloadUrl(item), '_blank');
                };

                const removeFile = (event: MouseEvent) => {
                    event.stopPropagation();
                    openFileDeleteDialog(item);
                };

                return (
                    <div
                        key={item.id}
                        data-key={item.id}
                        className="item-wrapper"
                        onClick={() => onClick(item)}
                        onMouseEnter={e => handleMouseEnter(e, item)}
                        onMouseLeave={e => handleMouseLeave(e, item)}>
                        <div
                            className={`item-img ${(selected || sameFileId) ? 'selected' : ''}`}
                            style={{
                                backgroundImage: `url(${buildImageUrl(item, null, 500)})`,
                                borderColor: theme.palette.primary.main,
                            }}>
                            {!item.mime_type?.includes('image') && (
                                <Stack spacing={1} alignItems="center">
                                    <FileOutlined style={{ fontSize: 30 }} />
                                    <Typography>
                                        {/* @ts-ignore */}
                                        {item.original_name ?? item.item_name}
                                    </Typography>
                                </Stack>
                            )}
                            <div className="blur-bg" />
                            {colorShiftOnHover && (
                                <div
                                    className="color-overlay"
                                    style={{
                                        background: `linear-gradient(45deg, ${theme.palette.primary.main}aa, ${theme.palette.secondary.main}aa)`,
                                    }}
                                />
                            )}
                            <div className="info-overlay">
                                <div className="info-col">
                                    {item.mime_type?.includes('image') && (
                                        <Typography
                                            variant="subtitle1"
                                            className="cutTextEnd"
                                            sx={{ width: '100%' }}
                                            color={theme.palette.common.white}>
                                            {/* @ts-ignore */}
                                            {item.original_name ?? item.item_name}
                                        </Typography>
                                    )}
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        spacing={1}
                                        flexWrap="wrap">
                                        {selected ? (
                                            <Chip
                                                color="success"
                                                size="small"
                                                label={<SelectOutlined />}
                                            />
                                        ) : (
                                            sameFileId && (
                                                <Chip
                                                    color="success"
                                                    size="small"
                                                    label={<ReloadOutlined />}
                                                />
                                            )
                                        )}
                                        {item.content_name && (
                                            <Chip
                                                color="primary"
                                                size="small"
                                                sx={{ maxWidth: '50%' }}
                                                label={
                                                    <Typography
                                                        variant="caption"
                                                        color={
                                                            theme.palette.common
                                                                .white
                                                        }>
                                                        {item.content_name}
                                                    </Typography>
                                                }
                                            />
                                        )}
                                        <Chip
                                            color="secondary"
                                            size="small"
                                            label={
                                                <Typography
                                                    variant="caption"
                                                    color={
                                                        theme.palette.common
                                                            .white
                                                    }>
                                                    {item.mime_type}
                                                </Typography>
                                            }
                                        />
                                    </Stack>
                                </div>
                                <div className="info-row">
                                    {item.creator && (
                                        <UserItem
                                            user={item.creator}
                                            trackUserActivity={false}>
                                            <Typography variant="caption">
                                                {formatDate(item.created_at)}
                                            </Typography>
                                        </UserItem>
                                    )}
                                    <Stack
                                        direction="row"
                                        alignItems="center"
                                        justifyContent="center"
                                        zIndex={2}
                                        sx={{ minWidth: 75 }}>
                                        <IconButton
                                            color="inherit"
                                            onClick={downloadFile}>
                                            <CloudDownloadOutlined />
                                        </IconButton>
                                        <IconButton
                                            color="error"
                                            onClick={removeFile}>
                                            <DeleteOutlined />
                                        </IconButton>
                                    </Stack>
                                </div>
                            </div>
                        </div>
                    </div>
                );
            })}
            {children}
        </div>
    );
};

export default MasonryFileList;
